import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { ArrowBack, Check, GroupAdd, Place } from '@mui/icons-material';
import AppNavigation from './AppNavigation';
import SignInNudge from './SignInNudge';
import PostCard from './PostCard';
import DiscoverGroupsOrClubsPanel from './DiscoverGroupsOrClubsPanel';
import EventsPanel from './EventsPanel';
import DiverRow from './DiverRow';
import InviteButton from './InviteButton';
import { useNotification } from './NotificationContext';
import { authService } from '../services/authService';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { followService } from '../services/followService';
import { userProfileService, RegionMembers } from '../services/userProfileService';
import { postService, Post } from '../services/postService';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { buildRegions, countrySlug, hasJoined, isLocalTo, regionLabel } from '../utils/regions';

/**
 * A region's community page. Members come in two tiers: local divers (anyone whose home site is
 * inside the region — implicit, nothing stored) and divers who chose to join it (stored on their
 * preferences as joinedRegions). The feed is the Public posts of both; the groups/clubs tab
 * searches by the region's name.
 */
const RegionCommunity: React.FC = () => {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { allSites, loading: sitesLoading } = useDiveSiteCatalog();
  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;
  const [tab, setTab] = useState(0);

  const [members, setMembers] = useState<RegionMembers | null>(null);
  const [loadingDivers, setLoadingDivers] = useState(true);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [followedIds, setFollowedIds] = useState<Set<string>>(new Set());
  const [myProfile, setMyProfile] = useState<UserPublicProfile | null>(null);
  const [joinBusy, setJoinBusy] = useState(false);

  const regions = useMemo(() => buildRegions(allSites), [allSites]);
  const region = useMemo(() => regions.find(r => r.slug === slug) ?? null, [regions, slug]);

  // The server's region index answers "who is in this community" directly — no downloading every diver.
  // Waits for the catalog so the region's country is known (that drives the wider "elsewhere in the country" tier).
  const regionCountry = region?.country ?? '';
  const regionKnown = !!region;
  useEffect(() => {
    if (!userId) { setLoadingDivers(false); return; }
    if (!regionKnown) return;
    let cancelled = false;
    setLoadingDivers(true);
    userProfileService.getRegionMembers(slug, regionCountry ? countrySlug(regionCountry) : undefined)
      .then(m => { if (!cancelled) setMembers(m); })
      .catch(() => { if (!cancelled) setMembers(null); })
      .finally(() => { if (!cancelled) setLoadingDivers(false); });
    return () => { cancelled = true; };
  }, [userId, slug, regionKnown, regionCountry]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    buddyService.getUserProfile(userId)
      .then(p => { if (!cancelled) setMyProfile(p); })
      .catch(() => { /* the Join button just stays hidden until the profile loads */ });
    return () => { cancelled = true; };
  }, [userId]);

  const byDiveCount = (list: UserPublicProfile[] | undefined) =>
    [...(list ?? [])].sort((a, b) => (b.diveCount ?? 0) - (a.diveCount ?? 0));
  const localDivers = useMemo(() => byDiveCount(members?.locals), [members]);
  // Divers who joined the community without living there.
  const joinedDivers = useMemo(() => byDiveCount(members?.joined), [members]);

  const iAmLocal = !!region && !!myProfile && isLocalTo(myProfile, region);
  const iHaveJoined = !!region && !!myProfile && hasJoined(myProfile, region);
  // Stored membership, which unlike `iHaveJoined` also covers a home-region diver who has hosted here.
  const isOnMyJoinedList = !!region && (myProfile?.joinedRegionSlugs ?? []).includes(region.slug);

  // The wider country tier: divers whose home site is in another region of the same country.
  const countryDivers = useMemo(() => byDiveCount(members?.country), [members]);

  // The feed covers everyone in the community — locals first, then those who joined. The server picks the
  // authors from the region index.
  useEffect(() => {
    if (!userId || !regionKnown) { setPosts([]); return; }
    let cancelled = false;
    setLoadingPosts(true);
    postService.getCommunityFeed({ regionSlug: slug })
      .then(p => { if (!cancelled) setPosts(p); })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setLoadingPosts(false); });
    return () => { cancelled = true; };
  }, [userId, slug, regionKnown]);

  const handleToggleJoin = useCallback(async () => {
    if (!region || !myProfile) return;
    const joining = !iHaveJoined;
    setJoinBusy(true);
    try {
      if (joining) await userProfileService.joinRegion(region.slug);
      else await userProfileService.leaveRegion(region.slug);
      setMyProfile(p => p && {
        ...p,
        joinedRegionSlugs: joining
          ? [...(p.joinedRegionSlugs ?? []), region.slug]
          : (p.joinedRegionSlugs ?? []).filter(s => s !== region.slug),
      });
    } catch {
      showError(joining ? 'Could not join this community' : 'Could not leave this community');
    } finally {
      setJoinBusy(false);
    }
  }, [region, myProfile, iHaveJoined, showError]);

  // Hosting needs membership (the server checks the joined list, since it can't derive a home-site region).
  // A home-region diver is already a member in spirit, so hosting quietly joins them — the dialog says so.
  const ensureMemberToHost = useCallback(async (): Promise<boolean> => {
    if (!region || !myProfile) return false;
    if (isOnMyJoinedList) return true;
    try {
      await userProfileService.joinRegion(region.slug);
      setMyProfile(p => p && { ...p, joinedRegionSlugs: [...(p.joinedRegionSlugs ?? []), region.slug] });
      return true;
    } catch {
      showError('Could not join this community, so the event was not created');
      return false;
    }
  }, [region, myProfile, isOnMyJoinedList, showError]);

  const handleFollow = useCallback(async (diver: UserPublicProfile) => {
    try {
      await followService.follow(diver.userId);
      setFollowedIds(prev => new Set(prev).add(diver.userId));
    } catch {
      showError(`Could not follow ${diver.displayName}`);
    }
  }, [showError]);

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="join this dive community"
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  if (sitesLoading && !region) {
    return <Box><AppNavigation /><Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box></Box>;
  }

  if (!region) {
    return (
      <Box>
        <AppNavigation />
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6">We couldn't find that community</Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate('/communities')}>Browse all communities</Button>
        </Box>
      </Box>
    );
  }


  const renderDiver = (diver: UserPublicProfile) => (
    <DiverRow
      key={diver.userId}
      diver={diver}
      isSelf={diver.userId === userId}
      followed={followedIds.has(diver.userId) || diver.isFollowing}
      onFollow={handleFollow}
    />
  );

  const label = regionLabel(region);
  // Others in the community plus the viewer when they belong (the divers list never includes the viewer).
  const memberCount = localDivers.length + joinedDivers.length + (iAmLocal || iHaveJoined ? 1 : 0);
  const featuredSites = [...region.sites].sort((a, b) => Number(!!b.featured) - Number(!!a.featured)).slice(0, 40);

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 760, mx: 'auto' }}>
        <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/communities')} sx={{ mt: 2 }}>All communities</Button>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1, mb: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>{label}</Typography>
          <InviteButton />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip size="small" variant="outlined" label={`${region.sites.length} dive site${region.sites.length === 1 ? '' : 's'}`} />
          {region.country && (
            <Chip size="small" variant="outlined" icon={<Place />} label={region.country} clickable onClick={() => navigate(`/countries/${countrySlug(region.country)}`)} />
          )}
          <Chip size="small" color="primary" variant="outlined" label={`${memberCount} member${memberCount === 1 ? '' : 's'}`} />
          <Box sx={{ flexGrow: 1 }} />
          {myProfile && (iAmLocal ? (
            <Chip size="small" color="primary" icon={<Check />} label="Your home region" />
          ) : (
            <Button
              size="small"
              variant={iHaveJoined ? 'outlined' : 'contained'}
              startIcon={iHaveJoined ? <Check /> : <GroupAdd />}
              disabled={joinBusy}
              onClick={handleToggleJoin}
            >
              {iHaveJoined ? 'Joined' : 'Join community'}
            </Button>
          ))}
        </Stack>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab label="Feed" />
          <Tab label="Events" />
          <Tab label="Members" />
          <Tab label="Dive sites" />
          <Tab label="Groups & clubs" />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Public posts from the last 30 days by divers based in {label} and divers who joined this community.
            </Typography>
            {(loadingPosts || loadingDivers) && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
            {!loadingPosts && !loadingDivers && posts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>No public posts here yet</Typography>
                <Typography variant="body2" color="text.secondary">
                  Set {label.split(',')[0]} as your home site and post publicly to start the conversation.
                </Typography>
              </Box>
            )}
            {posts.map(post => (
              <PostCard key={post.postId} post={post} currentUserId={userId} onDelete={() => setPosts(prev => prev.filter(p => p.postId !== post.postId))} />
            ))}
          </Box>
        )}

        {tab === 1 && (
          <EventsPanel
            regionSlug={region.slug}
            regionName={label}
            currentUserId={userId}
            ensureRegionMember={ensureMemberToHost}
            willJoinOnHost={!isOnMyJoinedList}
          />
        )}

        {tab === 2 && (
          <Stack spacing={1}>
            {loadingDivers && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
            {!loadingDivers && localDivers.length === 0 && joinedDivers.length === 0 && countryDivers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No divers list a home site here yet — be the first, or invite a buddy.
              </Typography>
            )}
            {localDivers.length > 0 && <Typography variant="overline" color="text.secondary">Local divers</Typography>}
            {localDivers.map(renderDiver)}
            {members && members.localCount - localDivers.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Showing {localDivers.length} of {members.localCount} local divers.
              </Typography>
            )}
            {joinedDivers.length > 0 && (
              <>
                <Typography variant="overline" color="text.secondary" sx={{ pt: localDivers.length > 0 ? 2 : 0 }}>
                  Joined this community
                </Typography>
                {joinedDivers.map(renderDiver)}
                {members && members.joinedCount - joinedDivers.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Showing {joinedDivers.length} of {members.joinedCount} who joined.
                  </Typography>
                )}
              </>
            )}
            {countryDivers.length > 0 && (
              <>
                <Typography variant="overline" color="text.secondary" sx={{ pt: 2 }}>
                  Elsewhere in{' '}
                  <Box component={RouterLink} to={`/countries/${countrySlug(region.country)}`} sx={{ color: 'inherit' }}>{region.country}</Box>
                </Typography>
                {countryDivers.map(renderDiver)}
              </>
            )}
          </Stack>
        )}

        {tab === 3 && (
          <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
            {featuredSites.map(site => (
              <Chip
                key={site.slug}
                icon={<Place />}
                label={site.name}
                variant={site.featured ? 'filled' : 'outlined'}
                color={site.featured ? 'primary' : 'default'}
                component={RouterLink}
                to={`/dive-sites/${site.slug}`}
                clickable
              />
            ))}
          </Stack>
        )}

        {tab === 4 && (
          <Stack spacing={2}>
            <Typography variant="caption" color="text.secondary">
              Showing groups and clubs whose name matches "{region.region}". Don't see yours? You can start one from the Groups or Clubs page.
            </Typography>
            <DiscoverGroupsOrClubsPanel kind="group" initialQuery={region.region} />
            <DiscoverGroupsOrClubsPanel kind="club" initialQuery={region.region} />
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default RegionCommunity;
